/**
 * The welcome intro plays on the page load that finishes signing in, and on
 * no other. Two signals say so:
 *  - a marker the sign-in page leaves in sessionStorage right before it
 *    reloads (password sign-in) or leaves for the IdP (SSO, hosted sign-in);
 *    it survives that redirect in the same tab and is cleared on first read,
 *    so a refresh afterwards finds nothing;
 *  - `code` on the entry URL, the IdP redirect itself, for sign-ins that did
 *    not start on Studio's sign-in page. The auth SDKs strip it right away.
 * index.html reads the same marker to paint the intro's first frame.
 */
export const SIGN_IN_MARKER_KEY = 'studio:welcome-intro';

// A marker older than this was left by a sign-in that never finished.
const MARKER_MAX_AGE_MS = 15 * 60 * 1000;

export function isSignInRedirect(search: string): boolean {
  return Boolean(new URLSearchParams(search).get('code')?.trim());
}

export function isFreshMarker(value: string | null, now: number): boolean {
  const startedAt = Number(value);
  return Number.isFinite(startedAt) && startedAt > 0 && now - startedAt < MARKER_MAX_AGE_MS;
}

/** Call right before the sign-in page reloads or redirects to finish signing in. */
export function rememberSignInStarted() {
  try {
    window.sessionStorage.setItem(SIGN_IN_MARKER_KEY, String(Date.now()));
  } catch {
    // Storage blocked: the IdP redirect's `code` still plays the intro.
  }
}

function takeSignInMarker(): boolean {
  try {
    const value = window.sessionStorage.getItem(SIGN_IN_MARKER_KEY);
    window.sessionStorage.removeItem(SIGN_IN_MARKER_KEY);
    return isFreshMarker(value, Date.now());
  } catch {
    return false;
  }
}

// Read at module load: the auth SDKs rewrite the URL as soon as they process
// the callback, before any component gets to look.
export const ARRIVED_FROM_SIGN_IN =
  typeof window !== 'undefined' && (takeSignInMarker() || isSignInRedirect(window.location.search));
